import { useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import type { EmblaOptionsType } from "embla-carousel";

/*
 * An autoplaying Embla carousel that yields to the visitor: any manual move
 * (arrow click or swipe) stops it, and it only resumes once they have been
 * idle for `cooldown` ms. Repeated interaction restarts the timer rather than
 * queueing several restarts.
 */
export function useCooldownCarousel(
  options: EmblaOptionsType,
  { delay = 4000, cooldown = 5000 }: { delay?: number; cooldown?: number } = {},
) {
  const autoplay = useRef(Autoplay({ delay, stopOnInteraction: false }));
  const timer = useRef<number | undefined>(undefined);

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [autoplay.current]);

  const hold = useCallback(() => {
    autoplay.current.stop();
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => autoplay.current.play(), cooldown);
  }, [cooldown]);

  /* Covers drag/swipe. The arrow handlers below call `hold` directly. */
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("pointerDown", hold);
    return () => {
      emblaApi.off("pointerDown", hold);
      window.clearTimeout(timer.current);
    };
  }, [emblaApi, hold]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
    hold();
  }, [emblaApi, hold]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
    hold();
  }, [emblaApi, hold]);

  return { emblaRef, emblaApi, scrollPrev, scrollNext };
}
